import { useState } from 'react';

import Swal from 'sweetalert2';

import {
    Avatar, Box, Card, CardContent, CardHeader, Chip, Grid,
    IconButton, ListItemIcon, ListItemText, Menu, MenuItem,
    Rating, Stack, Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const formatReviewDate = value => {
    if (!value)
        return 'Fecha desconocida';

    const date = new Date(value);

    if (Number.isNaN(date.getTime()))
        return 'Fecha desconocida';

    return new Intl.DateTimeFormat('es-MX', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'America/Mexico_City'
    }).format(date);
};

const ProductReview = ({
    review,
    isOwner = false,
    onDelete,
    onEdit
}) => {
    const [showComment, setShowComment] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const {
        comment,
        created_at,
        display_name,
        edited_at,
        images = [],
        admin_reply,
        rating,
        variant,
        verified_purchase
    } = review;
    const shortComment = comment.length > 400 && !showComment ?
        `${comment.substring(0, 400)}...`
    :
        comment;

    const confirmDelete = () => {
        setAnchorEl(null);
        Swal.fire({
            title: '¿Deseas eliminar tu reseña?',
            text: 'Dejará de mostrarse en la tienda.',
            showCancelButton: true,
            confirmButtonText: 'Eliminar',
            cancelButtonText: 'Cancelar'
        }).then(result => {
            if (result.isConfirmed)
                onDelete(review);
        });
    };

    return (
        <Card sx={{
            width: '100%',
            mt: 2,
            boxShadow: '0 2px 4px 2px rgb(0 0 0 / 20%)'
        }}>
            <CardHeader
                avatar={
                    <Avatar sx={{ bgcolor: '#2699FB' }}>
                        {Array.from(display_name || 'C')[0]}
                    </Avatar>
                }
                action={isOwner ?
                    <>
                        <IconButton
                            aria-label="Opciones de mi reseña"
                            onClick={event => setAnchorEl(event.currentTarget)}
                        >
                            <MoreVertIcon />
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={() => setAnchorEl(null)}
                        >
                            <MenuItem onClick={() => {
                                setAnchorEl(null);
                                onEdit(review);
                            }}>
                                <ListItemIcon>
                                    <EditIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Editar</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={confirmDelete}>
                                <ListItemIcon>
                                    <DeleteIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Eliminar reseña</ListItemText>
                            </MenuItem>
                        </Menu>
                    </>
                : null}
                title={display_name}
                subheader={`${formatReviewDate(created_at)}${
                    edited_at ? ' · Editada' : ''
                }`}
            />
            <CardContent>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                >
                    <Rating value={rating} readOnly />
                    {verified_purchase &&
                        <Chip
                            color="success"
                            label="Compra verificada"
                            size="small"
                            variant="outlined"
                        />
                    }
                    {variant && variant.title &&
                        <Chip
                            label={variant.title}
                            size="small"
                            variant="outlined"
                        />
                    }
                </Stack>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2, whiteSpace: 'pre-wrap' }}
                >
                    {shortComment}
                </Typography>
                {comment.length > 400 &&
                    <Box sx={{ mt: 1 }}>
                        <Typography
                            component="button"
                            onClick={() => setShowComment(value => !value)}
                            sx={{
                                border: 0,
                                background: 'none',
                                color: 'primary.main',
                                cursor: 'pointer',
                                p: 0
                            }}
                        >
                            {showComment ? 'Mostrar menos' : 'Mostrar más'}
                        </Typography>
                    </Box>
                }

                {images.length > 0 &&
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {images.map(image =>
                            <Grid item xl={3} lg={3} md={4} sm={4} xs={12}
                                key={image.id}>
                                <Box
                                    component="img"
                                    src={image.url}
                                    alt={image.alt}
                                    loading="lazy"
                                    sx={{
                                        borderRadius: 1,
                                        height: 180,
                                        objectFit: 'cover',
                                        width: '100%'
                                    }}
                                />
                            </Grid>
                        )}
                    </Grid>
                }

                {admin_reply &&
                    <Box sx={{
                        bgcolor: 'grey.100',
                        borderLeft: '4px solid',
                        borderColor: 'primary.main',
                        borderRadius: 1,
                        mt: 2,
                        p: 2
                    }}>
                        <Typography variant="subtitle2">
                            {admin_reply.author}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
                        >
                            {admin_reply.text}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 1 }}
                        >
                            {formatReviewDate(
                                admin_reply.updated_at ||
                                admin_reply.created_at
                            )}
                        </Typography>
                    </Box>
                }
            </CardContent>
        </Card>
    );
};

export default ProductReview;
